import { Helmet } from "react-helmet-async";

interface PageSEOProps {
  title: string;
  description: string;
  /** Omit on pages with no canonical URL of their own (e.g. the 404 page). */
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile";
  /** Array of JSON-LD schema objects to inject into <head>. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jsonLd?: Record<string, any>[];
  /** Optional noindex flag for thin/internal pages */
  noindex?: boolean;
}

const DEFAULT_OG_IMAGE = "https://marquettabryant.com/og-image.jpg";

/**
 * Per-page SEO component. Injects <title>, meta description, canonical,
 * Open Graph, Twitter card, and any JSON-LD schema into <head>.
 *
 * Usage:
 *   <PageSEO
 *     title="..."
 *     description="..."
 *     canonical="https://marquettabryant.com/..."
 *     jsonLd={[personSchema]}
 *   />
 */
const PageSEO = ({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  jsonLd = [],
  noindex = false,
}: PageSEOProps) => {
  const finalOgTitle = ogTitle ?? title;
  const finalOgDescription = ogDescription ?? description;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {canonical && <link rel="canonical" href={canonical} />}

      {noindex && <meta name="robots" content="noindex,follow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Marquetta Bryant" />
      <meta property="og:locale" content="en_US" />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="Marquetta Bryant, real estate broker, Mortgage Advisor and AI systems builder."
      />
      <meta
        property="article:author"
        content="https://www.threads.com/@marquetta_fvg"
      />
      <meta
        property="article:publisher"
        content="https://www.threads.com/@marquetta_fvg"
      />

      {/* Twitter / X (no @handle — uses Threads via article:author) */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta
        name="twitter:image:alt"
        content="Marquetta Bryant, real estate broker, Mortgage Advisor and AI systems builder."
      />

      {/* JSON-LD structured data */}
      {jsonLd.map((schema, i) => (
        <script
          key={i}
          type="application/ld+json"
          // Helmet serializes this literally into a <script> element
        >
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default PageSEO;
